import React, { useCallback, useState } from 'react'
import { Form } from 'semantic-ui-react'
import { GetCancelReservations } from '../../../api/services/pms'
import { useTextResources } from '../../../language'
import GridSearchTemplate from '../../../pages/GridSearchTemplate'
import { DatePicker } from '../../shared/DatePicker'
import HotelsDropDown from '../../shared/HotelsDropDown'
import moment from 'moment'
import { endpoint } from '../../../api/http'
import DropDown from '../../shared/dropdown'
import { Currencies } from '../../shared/currencies'
import { dateFormat, dateTimeFormat } from '../../shared/constants'

const CancelReservationsReport = () => {
    const text = useTextResources()
    const [dates, setDates] = useState([new Date(), new Date()])
    const [cancelByDateType, setCancelByDateType] = useState('CancelDate');

    const reservationFormatter = ({ value, row }) => {
        if (row) {
            return <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/Reservations/Index/${row.ReservationId}?hotelid=${row.HotelId}`}>
                {value}
            </a>
        } else {
            return value
        }
    }

    /* eslint-enable react/prop-types */
    const getDateCellValue = (row, columnName) => {
        const date = row[columnName];
        return date && moment(date).format(dateFormat);
    }

    const getDateTimeCellValue = (row, columnName) => {
        const date = row[columnName];
        return date && moment(date).format(dateTimeFormat);
    }

    const currencyFormatter = ({ value }) => {
        return value && `${value} ${getCurrencyByCurrentHotel()}`
    }

    const decimalFormatter = ({ value }) => {
        return value && value.toFixed(2);
    }

    const _cancelByDateTypeDropDown = ({ ...rest }) => {
        return <DropDown textField="text" valueField="value" selectFirst {...rest}
            data={[{
                text: text('CancelDate'),
                value: 'CancelDate'
            }, {
                text: text('CheckInDate'),
                value: 'CheckInDate'
            }]} />
    }

    const getFormatedDate = (cancelType, index) => {
        return dates &&
            cancelByDateType === cancelType ?
            moment(dates[index]).toISOString() :
            undefined
    }

    const getDisplayFormatedDate = (index) => {
        return dates &&
            moment(dates[index]).format(dateFormat)
    }

    const getCurrencyByCurrentHotel = useCallback(() => {
        const currencyCode = window.optima?.user?.CurrentHotel?.MainCurrencyId;
        return Currencies[currencyCode]?.symbol ?? '';
    })

    const customizeExcelHeader = worksheet => {
        const row = worksheet.addRow([`${text("CancelReservation")} ${getDisplayFormatedDate(0)} - ${getDisplayFormatedDate(1)}`])
        worksheet.mergeCells('A1:O1');
        row.height = 20
        row.font = { bold: true, size: 16 };
    }

    const gridOptions = {
        search: {
            title: text("CancelReservation"),
            fields: [
                <Form.Field label={text("Hotel")}
                    control={HotelsDropDown}
                    key="HotelIDs" clearable width="4"
                    allowSelectAll multiple />,
                <Form.Field label={text("CancelByDateType")}
                    control={_cancelByDateTypeDropDown}
                    key="CancelByDateType"
                    value={cancelByDateType}
                    onChange={setCancelByDateType} />,
                <DatePicker label={text("CancelDate")}
                    type="range" key="CancelDateRange"
                    value={dates} onChange={setDates} />
            ],
            api: GetCancelReservations,
            spec: {
                FromCancelDate: getFormatedDate('CancelDate', 0),
                ToCancelDate: getFormatedDate('CancelDate', 1),
                FromCheckInDate: getFormatedDate('CheckInDate', 0),
                ToCheckInDate: getFormatedDate('CheckInDate', 1),
                ReservationStatuses: 5,
                IncludeCanceled: true,
                IncludeAddons: true,
                IncludeTotalAmounts: true,
                IncludeReservationCompanyAgent: true,
                IncludeRoom: true,
                IncludeReservaionCustomerCommission: true,
                IncludeReservationCancellationReason: true,
                IncludeReservationDailyAccommodation: true,
                IncludeRoomType: true
            },
            groupSize: 6
        },
        grid: {
            columns: [
                { name: 'HotelName', title: text("HotelName") },
                { name: 'ReservationNumber', title: text('Reservation'), formatter: reservationFormatter },
                { name: 'CheckInDate', title: text("CheckInDate"), getCellValue: getDateCellValue },
                { name: 'CheckOutDate', title: text("CheckOutDate"), getCellValue: getDateCellValue },
                { name: 'Nights', title: text("Nights"), formatter: decimalFormatter },
                { name: 'CreationDate', title: text("CreateDate"), getCellValue: getDateCellValue },
                { name: 'ReservationCancelDayTime', title: text("CancelDayTime"), getCellValue: getDateTimeCellValue },
                { name: 'RoomTypeName', title: text("RoomType") },
                { name: 'ProductIncome', title: text("ProductIncome"), formatter: currencyFormatter },
                { name: 'ExtraAddonIncome', title: text("ExtraAddonIncome"), formatter: currencyFormatter },
                { name: 'TotalIncomeByHotelMainCurrency', title: text("TotalIncome"), formatter: currencyFormatter },
                { name: 'AgentName', title: text("AgentName") },
                { name: 'CompanyName', title: text("CompanyName") },
                { name: 'CancelationReason', title: text("CancellationReasonDescripiton") },
                { name: 'CancelDescription', title: text("CancelDescription") },
                { name: 'CancelUserName', title: text("CancelUserName") },
            ],
            summary: true,
            totalSummaryItems: [
                { columnName: 'ReservationNumber', type: 'count' },
                { columnName: 'Nights', type: 'avg' },
                { columnName: 'Nights', type: 'sum' },
                { columnName: 'ProductIncome', type: 'sum' },
                { columnName: 'ExtraAddonIncome', type: 'sum' },
                { columnName: 'TotalIncomeByHotelMainCurrency', type: 'sum' },
            ],
            export: true,
            excel: {
                customizeHeader: customizeExcelHeader,
                totalSummaryItems: [
                    { columnName: 'ReservationNumber', type: 'count' },
                    { columnName: 'Nights', type: 'avg' },
                    { columnName: 'Nights', type: 'sum' },
                    { columnName: 'ProductIncome', type: 'sum' },
                    { columnName: 'ExtraAddonIncome', type: 'sum' },
                    { columnName: 'TotalIncomeByHotelMainCurrency', type: 'sum' },
                ],
            },
            scrollbarX: true
        },
    }
    return (<GridSearchTemplate options={gridOptions} />)
}
export default CancelReservationsReport
